import styled, { css } from 'styled-components';
import { token } from '@bynder/design-system';
import {
  containerWidth,
  device,
  fontSizeRoot,
  PAGE_HEADER_SIZE,
} from '../../../constants/styles';
import { TypographyHeading } from '../../../types/typography';

import { fontNormal, getTypography } from './constants';
import { PageHeaderHeights } from '../../../types/headerHeights';
import { SectionLayout } from '../../../types';
import {
  StyledImage,
  StyledImageContainer,
} from '../../uploader/ImageWithProgress';

type WidgetListProps = {
  $sectionLayout: SectionLayout;
  $contentSpacing?: number;
  $isEmptyList: boolean;
};

export const MaxWidthContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  @media ${device.laptop} {
    width: ${containerWidth.small};
  }
`;

export const StyledPageContainer = styled(MaxWidthContainer)`
  font-size: ${fontSizeRoot};
  ${fontNormal}
`;

interface PageHeaderContentProps {
  $pageHeaderHeight: PageHeaderHeights;
  $width: number;
  $paddingLeft: number;
  $paddingRight: number;
}

export const StyledPageHeaderContent = styled(
  MaxWidthContainer,
)<PageHeaderContentProps>`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  font-size: ${fontSizeRoot};

  ${props =>
    !!props.$paddingLeft &&
    `
    padding-left: ${props.$paddingLeft}px;
  `}

  ${props =>
    !!props.$paddingRight &&
    `
    padding-right: ${props.$paddingRight}px;
  `}

  ${props =>
    props.$width &&
    `
    width: 100%;
    max-width: ${props.$width}px;
  `}

  @media ${device.laptop} {
    padding-top: ${token.spacing6};
    padding-bottom: ${token.spacing6};
  }
  > button:last-child {
    margin-bottom: ${token.spacing7};
  }

  ${({ $pageHeaderHeight }) => {
    if ($pageHeaderHeight === PageHeaderHeights.MEDIUM) {
      return css`
        min-height: ${PAGE_HEADER_SIZE.MEDIUM};
      `;
    }

    if ($pageHeaderHeight === PageHeaderHeights.LARGE) {
      return css`
        min-height: ${PAGE_HEADER_SIZE.LARGE};
      `;
    }

    return css`
      min-height: ${PAGE_HEADER_SIZE.DEFAULT};
    `;
  }}
`;

interface PageHeaderContainerProps {
  $coverUrl?: string;
  $backgroundColor?: string;
  $isActive: boolean;
}

export const StyledPageHeaderContainer = styled.div<PageHeaderContainerProps>`
  background-size: cover;
  background-position: center;
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor || 'transparent'};
  position: relative;

  & ${StyledImageContainer} {
    position: absolute;
    width: 100%;
  }
  & ${StyledImage} {
    max-height: 100%;
    object-fit: cover;
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      box-shadow: inset 0 0 0 3px ${token.brandPrimary500};
    `}

  ${({ $coverUrl }) =>
    $coverUrl &&
    css`
      background-image: url(${$coverUrl});
    `}
`;

interface StyledPageInfoProps {
  $color: string;
}

export const StyledPageInfo = styled.div<StyledPageInfoProps>`
  margin-right: ${token.spacing6};
  overflow-wrap: anywhere;
  width: 100%;
  z-index: 1;

  > p {
    ${props => getTypography(TypographyHeading.NORMAL, props.theme)};
    font-size: 1.6rem;
    line-height: 2em;
    margin: 0;
    ${({ $color }) =>
      $color &&
      css`
        color: ${$color};
      `}
  }
  > span {
    ${({ $color }) =>
      $color &&
      css`
        color: ${$color};
        opacity: 0.5;
      `}
  }
  > div {
    min-height: 22px;
  }
`;

export const StyledSectionMenu = styled.ul`
  font-size: ${token.fontSize75};
  line-height: 1.71;
  text-align: right;
  color: ${token.gray400a};
  max-height: 48px;
  align-items: center;
  border: 1px solid ${token.gray100a};
  border-radius: ${token.radiusLarge};
  padding: 20px 0;
  background-color: ${token.white};

  & > li {
    padding: ${token.spacing2};
    text-align: center;

    &:first-child {
      width: 70%;

      li {
        &:hover svg {
          .fill {
            fill: ${token.gray800a};
          }
          .stroke {
            stroke: ${token.gray800a};
          }
        }
      }
      & i {
        display: inline;
      }
    }
    &.separator {
      padding: 0;
      width: 1px;
      background-color: ${token.gray100a};
      height: 42px;
    }
    &:not(.separator):not(:first-child) {
      button {
        &:not([disabled]):hover {
          background-color: ${token.gray25a};
        }
      }
    }
  }
`;

export const StyledWidgetList = styled.ul<WidgetListProps>`
  position: relative;
  max-width: 100%;
  height: fit-content;
  flex: 1 1 0;
  ${props => props.$isEmptyList && `height: 100%;`}

  ${({ $contentSpacing }) => {
    return `
    > li:first-child:last-child {
    padding-bottom: 0px;
    padding-top: 0px;
  }
  > li {
    padding-bottom: ${$contentSpacing}px;
    padding-top: ${$contentSpacing}px;
  }
  > li:last-child {
    padding-top: ${$contentSpacing}px;
    padding-bottom: 0px;
  }
  > li:first-child {
    padding-top: 0px;
    padding-bottom: ${$contentSpacing}px;
  }
    `;
  }}

  ${props =>
    props.$sectionLayout === SectionLayout.DOUBLE_50_50 &&
    `width: calc(50% - ${token.spacing8} * 2);`}

  &:first-child {
    ${props =>
      props.$sectionLayout === SectionLayout.DOUBLE_66_33 &&
      `flex: 2 1 0;
       width: 66.66%;`}
  }

  &:last-child {
    ${props =>
      props.$sectionLayout === SectionLayout.DOUBLE_33_66 &&
      `flex: 2 1 0;
       width: 66.66%;`}
  }

  // Applying the rule for the 3 child columns
  &:nth-child(-n + 3) {
    ${props =>
      props.$sectionLayout === SectionLayout.TRIPLE_33_33_33 &&
      `width: 33.33%;`}
  }

  & > [data-react-beautiful-dnd-placeholder] {
    width: 100% !important;
  }
`;
